import {
  ArrowDownIcon,
  ArrowUpIcon,
  CogIcon,
  EyeIcon,
  EyeSlashIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import cloneDeep from 'clone-deep'
import {
  ISelectRangeOption,
  ISelectValueOption,
  IVisibleProp,
  loanCustomRangeOptionFields,
  loanCustomSelectOptionFields,
} from 'config'
import { getLoanInputFields } from 'config/loan.input.fields.constants'
import { convertNullToBlank } from 'pages/Resources/GuidelineOverlay/logic'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Input2, Select2, Tooltip } from 'stories/components'
import { isNull, removeComma } from 'utils'

import { generateTitle, generateTitleFromRange } from '../utils'
import { InterestOptions, InterestOptionsMap } from './constants'

export const SelectOptionsCustomRange = ({
  data,
  setData,
  fieldKey,
  errors,
  showError,
  promptVisibleLogic,
  showAdjustments = true,
}: {
  data: IVisibleProp
  setData: (data: IVisibleProp) => void
  fieldKey: string
  errors: Record<string, string>
  showError: boolean
  promptVisibleLogic: Function
  showAdjustments?: boolean
}) => {
  const { loanFieldProperties } = useSelector((state: any) => ({
    loanFieldProperties: state.globalConfigReducer[GlobalConfigType.LoanFields],
  }))
  const showCustomRangeInput = useMemo(() => loanCustomRangeOptionFields.includes(fieldKey), [fieldKey])
  const showCustomSelectInput = useMemo(() => loanCustomSelectOptionFields.includes(fieldKey), [fieldKey])
  const isMortgageLates = useMemo(() => fieldKey === 'mortgageLates', [fieldKey])
  const isPPP = useMemo(() => fieldKey === 'prepaymentPenalty', [fieldKey])
  const isLoanTerm = useMemo(() => fieldKey === 'loanTerm', [fieldKey])
  const isBankruptcy = useMemo(() => fieldKey === 'bankruptcy', [fieldKey])

  const valueStep = useMemo(() => {
    switch (fieldKey) {
      case 'loanTerm':
        return 6
      case 'prepaymentPenalty':
        return 12
      default:
        return 1
    }
  }, [fieldKey])

  if (!showCustomRangeInput && !showCustomSelectInput) return null

  const onAddOption = () => {
    let newData = cloneDeep(data)
    if (!newData.options) newData.options = []

    if (isMortgageLates) {
      let newFromValue = NaN
      let newToValue = NaN
      let newDays = NaN
      let newMonths = NaN
      if (newData.options.length) {
        const { from, to, days, months } = newData.options[newData.options.length - 1] as ISelectRangeOption
        if (!isNull(from)) newFromValue = Number(from) + 1
        if (!isNull(to)) newToValue = Number(to) + 1
        if (!isNull(days) && days !== undefined) newDays = days
        if (!isNull(months) && months !== undefined) newMonths = months
      }
      ;(newData.options as ISelectRangeOption[]).push({
        title: '',
        days: newDays,
        months: newMonths,
        from: newFromValue,
        to: newToValue,
      } as ISelectRangeOption)
    } else if (isBankruptcy) {
      let newFromValue = NaN
      let newChapter = NaN
      if (newData.options.length) {
        const { to, chapter } = newData.options[newData.options.length - 1] as ISelectRangeOption
        if (!isNull(to)) newFromValue = Number(to) + 1
        if (!isNull(chapter) && chapter !== undefined) newChapter = chapter
      }
      ;(newData.options as ISelectRangeOption[]).push({
        title: '',
        chapter: newChapter,
        from: newFromValue,
        to: NaN,
      } as ISelectRangeOption)
    } else if (showCustomRangeInput) {
      let newFromValue = NaN
      if (newData.options.length) {
        const { to } = newData.options[newData.options.length - 1] as ISelectRangeOption
        if (!isNull(to)) newFromValue = Number(to) + 1
      }
      ;(newData.options as ISelectRangeOption[]).push({
        title: '',
        from: newFromValue,
        to: NaN,
      } as ISelectRangeOption)
    } else
      (newData.options as ISelectValueOption[]).push({
        title: '',
        value: !!newData.options.length
          ? (newData.options[newData.options.length - 1] as ISelectValueOption).value + valueStep
          : 0,
      } as ISelectValueOption)

    const lastIndex = newData.options.length - 1
    newData.options[lastIndex].title = generateTitleFromRange(newData.options[lastIndex], fieldKey)

    setData(newData)
  }

  const [adjustmentDrafts, setAdjustmentDrafts] = useState<Record<string, string>>({})

  const getAdjustmentValue = (idx: number, field: 'LLPA' | 'LLRA', savedValue?: number) =>
    adjustmentDrafts[`${idx}-${field}`] ?? convertNullToBlank(savedValue)

  const onChangeAdjustmentDraft = (idx: number, field: 'LLPA' | 'LLRA', value: string) => {
    if (value !== '' && !/^-?\d*\.?\d*$/.test(value)) return
    setAdjustmentDrafts((drafts) => ({ ...drafts, [`${idx}-${field}`]: value }))
  }

  const onChangeControlOption = (
    idx: number,
    newValue:
      | { [P in keyof ISelectRangeOption]?: NonNullable<ISelectRangeOption[P]> }
      | { [P in keyof ISelectValueOption]?: NonNullable<ISelectValueOption[P]> }
      | null,
  ) => {
    const newData = cloneDeep(data)
    if (!newData.options) return

    if (!newValue) newData.options.splice(idx, 1)
    else
      newData.options[idx] = {
        ...newData.options[idx],
        ...newValue,
      }

    if (newValue?.title === undefined && newValue !== null && ![-1, 1].includes(newValue as any)) {
      newData.options[idx].title = generateTitleFromRange(newData.options[idx], fieldKey)
    }

    setData(newData)
  }

  const onChangeControlOptionOrder = (idx: number, dir: 1 | -1) => {
    const newData = cloneDeep(data)
    if (!newData.options) return

    const newIdx = idx + dir
    if (newIdx < 0 || newIdx >= newData.options.length) return
    else {
      let temp = newData.options[idx]
      newData.options[idx] = newData.options[newIdx]
      newData.options[newIdx] = temp
    }

    setData(newData)
  }

  const onBlurAdjustment = (idx: number, field: 'LLPA' | 'LLRA', rawValue: string) => {
    const value = rawValue === '' || isNaN(Number(rawValue)) ? NaN : Number(rawValue)
    setAdjustmentDrafts((drafts) => {
      const nextDrafts = { ...drafts }
      delete nextDrafts[`${idx}-${field}`]
      return nextDrafts
    })
    onChangeControlOption(idx, { [field]: value })
  }

  const onChangeControlOptionVisibleLogic = async (idx: number) => {
    const newData = cloneDeep(data)
    if (!newData.options) return
    const result = await promptVisibleLogic(idx, {
      loanFieldProperties,
      fieldInputs: getLoanInputFields(),
      value: newData.options[idx].visibleLogic || [],
    })
    if (!result) return

    newData.options[idx].visibleLogic = result
    setData(newData)
  }

  const { options } = data

  return (
    <div className="grid gap-2">
      <div className="flex justify-between">
        <p className="font-bold text-sm">Custom Range Options</p>
        <span className="btn-icon" onClick={onAddOption}>
          <PlusIcon className="w-4 h-4" />
        </span>
      </div>
      {options &&
        options.map((opt, idx) => {
          const {
            title,
            chapter,
            from,
            to,
            days,
            months,
            visible,
            value,
            subValue,
            interestOption,
            monthsInterest,
            LLPA,
            LLRA,
          } = opt as any
          const isVisible = !!visible || visible === undefined
          const Icon = isVisible ? EyeIcon : EyeSlashIcon
          const titleValue = generateTitle(fieldKey, opt, idx)

          return (
            <div
              className={`grid ${
                isMortgageLates || isPPP
                  ? `grid-cols-${showAdjustments ? 8 : 6}`
                  : isBankruptcy
                  ? `grid-cols-${showAdjustments ? 7 : 5}`
                  : `grid-cols-${showAdjustments ? 6 : 4}`
              } gap-2`}
            >
              <div className="col-span-2">
                <Input2
                  title={titleValue}
                  value={title}
                  key={`Option-${idx}`}
                  placeholder={title}
                  className={isVisible ? 'opacity-100' : 'opacity-30'}
                  onChange={(value: string) => onChangeControlOption(idx, { title: value })}
                  readOnly={isPPP || isLoanTerm ? false : true}
                  additionalElements={() => (
                    <div className="flex gap-2">
                      <Tooltip message="Visible / Invisible">
                        <Icon
                          className="w-4 h-4 cursor-pointer"
                          onClick={() => onChangeControlOption(idx, { visible: !isVisible })}
                        />
                      </Tooltip>
                      <TrashIcon
                        className="w-4 h-4 cursor-pointer text-red-500"
                        onClick={() => onChangeControlOption(idx, null)}
                      />
                      <ArrowUpIcon
                        className="w-4 h-4 cursor-pointer text-shade-blue"
                        onClick={() => onChangeControlOptionOrder(idx, -1)}
                      />
                      <ArrowDownIcon
                        className="w-4 h-4 cursor-pointer text-shade-blue"
                        onClick={() => onChangeControlOptionOrder(idx, 1)}
                      />
                      <Tooltip message="Visible Logic">
                        <CogIcon
                          className="w-4 h-4 cursor-pointer text-shade-blue"
                          onClick={() => onChangeControlOptionVisibleLogic(idx)}
                        />
                      </Tooltip>
                    </div>
                  )}
                  required
                  error={showError && !title ? 'This field is required.' : ''}
                />
              </div>
              {showCustomRangeInput ? (
                <>
                  {isBankruptcy && (
                    <Input2
                      type="text"
                      title={`Chapter`}
                      value={convertNullToBlank(chapter)}
                      key={`Option-${idx}-chapter`}
                      placeholder={'Chapter'}
                      className={isVisible ? 'opacity-100' : 'opacity-30'}
                      onChange={(value: any) => {
                        if (value == '') value = NaN
                        else value = isNaN(Number(value)) ? NaN : Number(value)
                        onChangeControlOption(idx, { chapter: value })
                      }}
                    />
                  )}
                  <Input2
                    type="text"
                    title={`${isMortgageLates ? 'Times ' : ''}From`}
                    value={convertNullToBlank(from)}
                    key={`Option-${idx}-from`}
                    placeholder={'From'}
                    className={isVisible ? 'opacity-100' : 'opacity-30'}
                    onChange={(value: any) => {
                      if (value == '') value = NaN
                      else value = isNaN(Number(value)) ? NaN : Number(value)
                      onChangeControlOption(idx, { from: value })
                    }}
                  />
                  <Input2
                    type="text"
                    title={`${isMortgageLates ? 'Times ' : ''}To`}
                    value={convertNullToBlank(to)}
                    key={`Option-${idx}-to`}
                    placeholder={'To'}
                    className={isVisible ? 'opacity-100' : 'opacity-30'}
                    onChange={(value: any) => {
                      if (value == '') value = NaN
                      else value = isNaN(Number(value)) ? NaN : Number(value)
                      onChangeControlOption(idx, { to: value })
                    }}
                  />

                  {isMortgageLates && (
                    <>
                      <Input2
                        type="text"
                        title={`Late Days`}
                        value={convertNullToBlank(days)}
                        key={`Option-${idx}-days`}
                        placeholder={'Days'}
                        className={isVisible ? 'opacity-100' : 'opacity-30'}
                        onChange={(value: any) => {
                          if (value == '') value = NaN
                          else value = isNaN(Number(value)) ? NaN : Number(value)
                          onChangeControlOption(idx, { days: value })
                        }}
                      />
                      <Input2
                        type="text"
                        title={`Time Period`}
                        value={convertNullToBlank(months)}
                        key={`Option-${idx}-months`}
                        placeholder={'Months'}
                        className={isVisible ? 'opacity-100' : 'opacity-30'}
                        onChange={(value: any) => {
                          if (value == '') value = NaN
                          else value = isNaN(Number(value)) ? NaN : Number(value)
                          onChangeControlOption(idx, { months: value })
                        }}
                      />
                    </>
                  )}
                </>
              ) : (
                <>
                  <Input2
                    type="text"
                    title={`Value`}
                    value={convertNullToBlank(value)}
                    key={`Option-${idx}-to`}
                    placeholder={'Value'}
                    className={isVisible ? 'opacity-100' : 'opacity-30'}
                    onChange={(value: any) => {
                      if (value == '') value = NaN
                      else value = isNaN(Number(value)) ? NaN : Number(value)
                      onChangeControlOption(idx, { value: value })
                    }}
                  />

                  {isPPP && (
                    <>
                      <Input2
                        type="text"
                        title={`PPP Structure`}
                        value={subValue}
                        key={`Option-${idx}-subValue`}
                        placeholder={'5/4/3/2/1'}
                        className={isVisible ? 'opacity-100' : 'opacity-30'}
                        onChange={(v: any) => {
                          const raw = v.replace(/[^0-9]/g, '')
                          if (raw.length > (Math.floor(removeComma(value) / 12) || 1)) return
                          const formatted = raw.split('').join('/')
                          onChangeControlOption(idx, { subValue: formatted })
                        }}
                      />
                      <Select2
                        id={`Option-${idx}-interestOption`}
                        title={`Interest Option`}
                        options={InterestOptionsMap}
                        value={interestOption}
                        key={`Option-${idx}-interestOption`}
                        hasDefaultOption={true}
                        className={isVisible ? 'opacity-100' : 'opacity-30'}
                        onChange={(value: any) => onChangeControlOption(idx, { interestOption: value })}
                      />
                      <Input2
                        type="text"
                        title={`Months Interest`}
                        value={
                          interestOption == InterestOptions.InterestBased ? convertNullToBlank(monthsInterest) : ''
                        }
                        disabled={interestOption != InterestOptions.InterestBased}
                        key={`Option-${idx}-monthsInterest`}
                        className={isVisible ? 'opacity-100' : 'opacity-30'}
                        onChange={(value: any) => {
                          if (value == '') value = NaN
                          else value = isNaN(Number(value)) ? NaN : Number(value)
                          onChangeControlOption(idx, { monthsInterest: value })
                        }}
                      />
                    </>
                  )}
                </>
              )}

              {showAdjustments && (
                <>
                  <Input2
                    type="text"
                    title="LLPA"
                    value={getAdjustmentValue(idx, 'LLPA', LLPA)}
                    key={`llpa-${idx}`}
                    className={isVisible ? 'opacity-100' : 'opacity-30'}
                    onChange={(value: string) => onChangeAdjustmentDraft(idx, 'LLPA', value)}
                    onBlur={(value: string) => onBlurAdjustment(idx, 'LLPA', value)}
                  />
                  <Input2
                    type="text"
                    title="LLRA"
                    value={getAdjustmentValue(idx, 'LLRA', LLRA)}
                    key={`llra-${idx}`}
                    className={isVisible ? 'opacity-100' : 'opacity-30'}
                    onChange={(value: string) => onChangeAdjustmentDraft(idx, 'LLRA', value)}
                    onBlur={(value: string) => onBlurAdjustment(idx, 'LLRA', value)}
                  />
                </>
              )}
            </div>
          )
        })}
      {errors['customRangeSelectOptions'] && (
        <p className="text-sm text-rose-700">{errors['customRangeSelectOptions']}</p>
      )}
    </div>
  )
}
